import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/gtax/support`;
const KEY = "gtax_support_sid";

const Bubble = ({ m }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[82%] px-4 py-3 text-[15px] leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#8F6B22] text-white" : "bg-[color:var(--bg)] border border-[color:var(--line)]"}`}>{m.text}</div>
  </div>
);

const Typing = () => (
  <div className="flex gap-1 px-4 py-3 border border-[color:var(--line)] w-16" data-testid="support-typing">
    {[0, 1, 2].map((i) => <span key={i} className="w-1.5 h-1.5 rounded-full bg-[color:var(--mute)] animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />)}
  </div>
);

const Intro = ({ start, busy }) => {
  const [f, setF] = useState({ name: "", email: "", reference: "" });
  const [member, setMember] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const input = "w-full border border-[color:var(--line)] bg-transparent px-3 py-2 text-sm";
  return (
    <div className="p-5 space-y-3" data-testid="support-intro">
      <p className="text-sm text-[color:var(--mute)]">Member Services usually replies within a minute.</p>
      <input className={input} placeholder="Your name" value={f.name} onChange={set("name")} data-testid="support-name" />
      <input className={input} placeholder="Email (for a copy of our reply)" value={f.email} onChange={set("email")} data-testid="support-email" />
      <label className="flex gap-2 items-center text-sm cursor-pointer"><input type="checkbox" checked={member} onChange={() => setMember(!member)} data-testid="support-member-toggle" /> I'm a member</label>
      {member && <input className={input} placeholder="Your GTX reference" value={f.reference} onChange={set("reference")} data-testid="support-reference" />}
      <button className="gx-btn w-full" disabled={busy} onClick={() => start(member ? f : { ...f, reference: "" })} data-testid="support-start">Start chat</button>
    </div>
  );
};

export const SupportChat = () => {
  const [open, setOpen] = useState(false);
  const [chat, setChat] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  useEffect(() => {
    const sid = localStorage.getItem(KEY);
    if (sid) axios.get(`${API}/${sid}`).then((r) => setChat(r.data)).catch(() => localStorage.removeItem(KEY));
  }, []);
  useEffect(() => {
    if (!open || chat?.status !== "human") return undefined;
    const id = setInterval(() => axios.get(`${API}/${chat.sid}`).then((r) => setChat(r.data)).catch(() => {}), 5000);
    return () => clearInterval(id);
  }, [open, chat?.status, chat?.sid]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [chat?.messages?.length, busy, open]);
  const start = async (f) => {
    setBusy(true);
    try { const { data } = await axios.post(`${API}/start`, f); localStorage.setItem(KEY, data.sid); setChat(data); } finally { setBusy(false); }
  };
  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || busy) return;
    setText("");
    setChat({ ...chat, messages: [...chat.messages, { role: "user", text: t, at: "" }] });
    setBusy(true);
    const t0 = Date.now();
    try {
      const { data } = await axios.post(`${API}/${chat.sid}/message`, { text: t });
      await new Promise((r) => setTimeout(r, Math.max(0, 1200 - (Date.now() - t0))));
      setChat(data);
    } catch {
      setChat((c) => ({ ...c, messages: [...c.messages, { role: "agent", text: "Sorry, the connection dropped. Please try again, or write to support@gta-x.io.", at: "" }] }));
    } finally { setBusy(false); }
  };
  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} className="fixed bottom-5 right-5 z-50 gx-btn !rounded-full !px-5 !py-4 shadow-xl inline-flex items-center gap-2" data-testid="support-open">
          <MessageCircle size={18} /> Chat with us
        </button>
      )}
      {open && (
        <div className="fixed bottom-0 right-0 sm:bottom-5 sm:right-5 z-50 w-full sm:w-[380px] h-[85vh] sm:h-[600px] flex flex-col gx-card shadow-2xl bg-[color:var(--bg2,var(--bg))]" data-testid="support-panel">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[color:var(--line)]">
            <div><div className="gx-caps text-base">GTA X Member Services</div>
              <div className="text-xs text-[color:var(--mute)] flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Online</div></div>
            <button onClick={() => setOpen(false)} aria-label="Close chat" data-testid="support-close"><X size={18} /></button>
          </div>
          {!chat ? <Intro start={start} busy={busy} /> : (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="support-messages">
                {chat.messages.map((m, i) => <Bubble key={i} m={m} />)}
                {busy && <Typing />}
                <div ref={end} />
              </div>
              <form onSubmit={send} className="flex border-t border-[color:var(--line)]">
                <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a message…" className="flex-1 bg-transparent px-4 py-4 text-sm outline-none" data-testid="support-input" />
                <button className="px-4 text-[color:var(--gold2)]" disabled={busy} aria-label="Send" data-testid="support-send"><Send size={18} /></button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
};
